import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { materialCreateFields } from "../../../configs/material";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";
import { ROLE, STORAGE } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import BackButton from "../../../shared/components/BackButton";
import CancelModal from "../../../shared/components/CancelModal";
import { api } from "../../../shared/hooks/api";
import useMergeState from "../../../shared/hooks/useMergeState";
import { error, success } from "../../../utils/toast";
import { ConversionRateField } from "../detail/ConversionRateField";
import { SingleMaterial } from "../single";
import ProviderLinkedList from "../single/MaterialList";
import { ContentWrapper } from "./Styled";
import CreateMaterialTabNavigation from "./TabBar";
import Title from "./Title";

const initialMaterialData = {
    name: "",
    quantity: 0,
    price: 1000,
    providers: [],
    storage: "",
};

// Get default storage based on role
const getDefaultStorage = (role: string): string => {
    switch (role) {
        case ROLE.FIRST_STORAGE_KEEPER:
            return STORAGE.FIRST;
        case ROLE.SECOND_STORAGE_KEEPER:
            return STORAGE.SECOND;
        default:
            return "";
    }
};

const CreateMaterial = () => {
    const navigate = useNavigate();
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const initialMaterialState = {
        ...initialMaterialData,
        storage: getDefaultStorage(role),
    };

    const location = useLocation();
    const [material, mergeMaterial] = useMergeState(initialMaterialState);
    const [{ isCreating }, createMaterial] = api.post("/materials/");
    const [validated, setValidated] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");

    useEffect(() => {
        if (location.state) {
            const newDuplicateData = {
                ...location.state.duplicateData,
                name: location.state.duplicateData.name + " - Copy",
                quantity: 0,
            };
            mergeMaterial(newDuplicateData);
        }
    }, [location.state]);

    // Mark as modified when material data changes
    useEffect(() => {
        if (material.name || material.providers.length > 0 || material.price !== 1000) {
            setIsModified(true);
        }
    }, [material]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setNavigationPath(path);
            setShowUnsavedDialog(true);
            return false;
        }
        
        navigate(path);
        return true;
    }, [isModified, navigate]);

    const handleBackClick = () => {
        confirmNavigation("/materials");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        await handleSubmit();
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };
    
    const handleSubmit = async () => {
        const providers = material.providers.map((provider: any) => ({
            provider_id: provider.id,
            price: provider.price,
        }));

        const responseData = {
            ...material,
            providers: providers,
        };
      
        await createMaterial(responseData)
            .then((data: any) => {
                success("Thành Công", "Tạo mới vật tư thành công");
                setIsModified(false);
                navigate(`/materials/${data.id}`);
                return true;
            })
            .catch((err: any) => {
                error("Lỗi tạo mới vật tư", err);
                return false;
            });
    };

    const handleChangeMaterialInfo = (updatedData: any) => {
        setIsModified(true);
        mergeMaterial({
            ...material,
            ...updatedData,
        });
    };

    return (
        <ContentWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title title={"Tạo mới vật tư"} setValidated={setValidated} submitForm={handleSubmit} isLoading={isCreating} />
            <CreateMaterialTabNavigation
                materialDetailTab={
                    <SingleMaterial
                        material={material}
                        mergeMaterial={handleChangeMaterialInfo}
                        setSubmit={setSubmitted}
                        validated={validated}
                        materialFields={[
                            ...materialCreateFields.slice(0, -1),
                            {
                                label: "Tỉ lệ chuyển đổi",
                                type: "custom",
                                key: "conversion_rate",
                                required: false,
                                customComponent: (
                                    <ConversionRateField material={material} />
                                ),
                            },
                            ...materialCreateFields.slice(-1),
                        ]}
                    />
                }
                providerDetailTab={<ProviderLinkedList materialData={material} mergeMaterial={handleChangeMaterialInfo} />}
            />

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />
        </ContentWrapper>
    );
};

export default CreateMaterial;
